import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, SquareDivide } from "lucide-react";
import { toast } from "sonner";

// ---- Conversion logic (deterministic, 1/64" precision) ---------------------

function gcd(a, b) { return b ? gcd(b, a % b) : a; }

export function reduceFraction(num, den) {
  const g = gcd(num, den) || 1;
  return [num / g, den / g];
}

// Parse '11/32', '3/4', '1-1/2', '1 1/2', '2', optional trailing '"'.
export function parseFraction(raw) {
  const s = (raw || "").trim().replace(/["\u2033]/g, "");
  if (!s) return null;
  const m = s.match(/^(\d+)(?:[\s-]+(\d+)\s*\/\s*(\d+))?$|^(\d+)\s*\/\s*(\d+)$/);
  if (!m) return null;
  if (m[4] !== undefined) {
    const den = Number(m[5]);
    if (!den) return null;
    return Number(m[4]) / den;
  }
  const whole = Number(m[1]);
  if (m[2] !== undefined) {
    const den = Number(m[3]);
    if (!den) return null;
    return whole + Number(m[2]) / den;
  }
  return whole;
}

// Decimal → nearest 1/64", standard oilfield notation (e.g. 11/32", 1-1/2").
export function decimalToFraction(value) {
  if (!Number.isFinite(value) || value < 0) return null;
  const total64ths = Math.round(value * 64);
  const whole = Math.floor(total64ths / 64);
  const rem = total64ths - whole * 64;
  if (rem === 0) return { text: `${whole}"`, exact: total64ths / 64 };
  const [n, d] = reduceFraction(rem, 64);
  const frac = `${n}/${d}`;
  return {
    text: whole > 0 ? `${whole}-${frac}"` : `${frac}"`,
    exact: total64ths / 64,
  };
}

const QUICK_REF = Array.from({ length: 64 }, (_, i) => {
  const [n, d] = reduceFraction(i + 1, 64);
  const v = (i + 1) / 64;
  return { frac: `${n}/${d}`, dec: v.toFixed(5), mm: (v * 25.4).toFixed(2) };
});

const toMm = (inches) => `${(inches * 25.4).toFixed(2)} mm`;

// Common oilfield bolting threads — UNC (coarse) + 8-UN constant-pitch series
// (API 6A studs use UNC below 1" and 8-UN at 1" and above).
const THREAD_REF = {
  UNC: [
    ['1/4"', 20], ['5/16"', 18], ['3/8"', 16], ['7/16"', 14], ['1/2"', 13],
    ['9/16"', 12], ['5/8"', 11], ['3/4"', 10], ['7/8"', 9], ['1"', 8],
  ],
  "8-UN": [
    ['1-1/8"', 8], ['1-1/4"', 8], ['1-3/8"', 8], ['1-1/2"', 8], ['1-5/8"', 8],
    ['1-3/4"', 8], ['1-7/8"', 8], ['2"', 8], ['2-1/8"', 8], ['2-1/4"', 8], ['2-1/2"', 8],
  ],
};

// ---- UI ---------------------------------------------------------------------

function fallbackCopy(text) {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { ok = false; }
  ta.remove();
  ok ? toast.success(`Copied ${text}`) : toast.error("Copy failed");
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`Copied ${text}`),
      () => fallbackCopy(text)
    );
  } else {
    fallbackCopy(text);
  }
}

export default function DecimalFractionConverter() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("f2d"); // "f2d" | "d2f" | "mm2f"
  const [fracInput, setFracInput] = useState("");
  const [decInput, setDecInput] = useState("");
  const [mmInput, setMmInput] = useState("");

  const decimalResult = useMemo(() => {
    const v = parseFraction(fracInput);
    return v === null ? null : v.toFixed(5);
  }, [fracInput]);

  const fractionResult = useMemo(() => {
    const s = decInput.trim();
    if (!s) return null;
    const v = Number(s);
    if (!Number.isFinite(v) || v < 0) return null;
    return decimalToFraction(v);
  }, [decInput]);

  const mmResult = useMemo(() => {
    const s = mmInput.trim();
    if (!s) return null;
    const v = Number(s);
    if (!Number.isFinite(v) || v < 0) return null;
    return decimalToFraction(v / 25.4);
  }, [mmInput]);

  return (
    <div className="flex flex-col gap-4" data-testid="fraction-converter">
      <section>
        <button type="button" data-testid="fraction-back-btn" onClick={() => navigate("/categories")}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3 h-3" /> Calculators
        </button>
        <h2 className="font-heading font-black text-2xl tracking-tight mt-1 flex items-center gap-2">
          <SquareDivide className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
          Decimal/Fractions Converter
        </h2>
        <p className="text-[12px] text-[var(--rs-text-2)] mt-1">
          Live conversion to the nearest 1/64&quot; — standard oilfield precision.
        </p>
      </section>

      <div className="flex gap-2" role="tablist">
        {[["f2d", "Frac → Dec"], ["d2f", "Dec → Frac"], ["mm2f", "mm → Frac"]].map(([m, label]) => (
          <button key={m} type="button" role="tab" aria-selected={mode === m}
            data-testid={`fraction-mode-${m}`}
            onClick={() => setMode(m)}
            className="flex-1 h-11 rounded-md font-semibold text-[11px] label-up border transition-colors"
            style={mode === m
              ? { background: "var(--rs-amber)", color: "var(--rs-bg)", borderColor: "var(--rs-amber)" }
              : { borderColor: "var(--rs-border)", color: "var(--rs-text-2)" }}>
            {label}
          </button>
        ))}
      </div>

      {mode === "f2d" && (
        <section className="card-rs p-4">
          <label className="block">
            <span className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1.5">Fraction</span>
            <input
              data-testid="fraction-input"
              value={fracInput}
              onChange={(e) => setFracInput(e.target.value)}
              placeholder={'e.g. 11/32 or 1-1/2'}
              inputMode="text"
              autoComplete="off"
              className="w-full h-14 rounded-md border bg-[var(--rs-surface)] px-3 text-xl font-mono-rs"
              style={{ borderColor: "var(--rs-border)" }}
            />
          </label>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div>
              <span className="block text-[10px] label-up text-[var(--rs-text-3)]">Decimal</span>
              <span className="text-2xl font-bold font-mono-rs" data-testid="decimal-result">
                {decimalResult ?? (fracInput.trim() ? "—" : "")}
              </span>
              {decimalResult && (
                <span className="block text-[12px] font-mono-rs" style={{ color: "var(--rs-amber)" }} data-testid="decimal-result-mm">
                  = {toMm(Number(decimalResult))}
                </span>
              )}
            </div>
            {decimalResult && (
              <button type="button" data-testid="copy-decimal-btn" onClick={() => copyText(decimalResult)}
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md font-semibold text-[12px] border"
                style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}>
                <Copy className="w-4 h-4" /> Copy
              </button>
            )}
          </div>
          {fracInput.trim() && decimalResult === null && (
            <p className="mt-2 text-[11px]" style={{ color: "rgb(252,165,165)" }} data-testid="fraction-parse-error">
              Enter a fraction like 11/32, 3/4 or a mixed number like 1-1/2.
            </p>
          )}
        </section>
      )}
      {mode === "d2f" && (
        <section className="card-rs p-4">
          <label className="block">
            <span className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1.5">Decimal (inches)</span>
            <input
              data-testid="decimal-input"
              value={decInput}
              onChange={(e) => setDecInput(e.target.value)}
              placeholder="e.g. 0.34375"
              inputMode="decimal"
              autoComplete="off"
              className="w-full h-14 rounded-md border bg-[var(--rs-surface)] px-3 text-xl font-mono-rs"
              style={{ borderColor: "var(--rs-border)" }}
            />
          </label>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div>
              <span className="block text-[10px] label-up text-[var(--rs-text-3)]">Nearest 1/64&quot;</span>
              <span className="text-2xl font-bold font-mono-rs" data-testid="fraction-result">
                {fractionResult ? fractionResult.text : (decInput.trim() ? "—" : "")}
              </span>
              {fractionResult && (
                <span className="block text-[12px] font-mono-rs" style={{ color: "var(--rs-amber)" }} data-testid="fraction-result-mm">
                  = {toMm(fractionResult.exact)}
                </span>
              )}
              {fractionResult && Math.abs(fractionResult.exact - Number(decInput)) > 1e-9 && (
                <span className="block text-[11px] text-[var(--rs-text-3)]" data-testid="fraction-rounded-note">
                  = {fractionResult.exact.toFixed(5)} (rounded to 1/64&quot;)
                </span>
              )}
            </div>
            {fractionResult && (
              <button type="button" data-testid="copy-fraction-btn" onClick={() => copyText(fractionResult.text)}
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md font-semibold text-[12px] border shrink-0"
                style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}>
                <Copy className="w-4 h-4" /> Copy
              </button>
            )}
          </div>
          {decInput.trim() && !fractionResult && (
            <p className="mt-2 text-[11px]" style={{ color: "rgb(252,165,165)" }} data-testid="decimal-parse-error">
              Enter a decimal like 0.34375 or 1.5.
            </p>
          )}
        </section>
      )}
      {mode === "mm2f" && (
        <section className="card-rs p-4">
          <label className="block">
            <span className="block text-[10px] label-up text-[var(--rs-text-3)] mb-1.5">Millimetres</span>
            <input
              data-testid="mm-input"
              value={mmInput}
              onChange={(e) => setMmInput(e.target.value)}
              placeholder="e.g. 8.73"
              inputMode="decimal"
              autoComplete="off"
              className="w-full h-14 rounded-md border bg-[var(--rs-surface)] px-3 text-xl font-mono-rs"
              style={{ borderColor: "var(--rs-border)" }}
            />
          </label>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div>
              <span className="block text-[10px] label-up text-[var(--rs-text-3)]">Nearest 1/64&quot;</span>
              <span className="text-2xl font-bold font-mono-rs" data-testid="mm-fraction-result">
                {mmResult ? mmResult.text : (mmInput.trim() ? "—" : "")}
              </span>
              {mmResult && (
                <span className="block text-[12px] font-mono-rs" style={{ color: "var(--rs-amber)" }} data-testid="mm-result-detail">
                  = {mmResult.exact.toFixed(5)}&quot; = {toMm(mmResult.exact)}
                </span>
              )}
            </div>
            {mmResult && (
              <button type="button" data-testid="copy-mm-fraction-btn" onClick={() => copyText(mmResult.text)}
                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md font-semibold text-[12px] border shrink-0"
                style={{ borderColor: "var(--rs-border)", color: "var(--rs-amber)" }}>
                <Copy className="w-4 h-4" /> Copy
              </button>
            )}
          </div>
          {mmInput.trim() && !mmResult && (
            <p className="mt-2 text-[11px]" style={{ color: "rgb(252,165,165)" }} data-testid="mm-parse-error">
              Enter a millimetre value like 8.73 or 38.1.
            </p>
          )}
        </section>
      )}

      <section className="card-rs p-4" data-testid="fraction-quick-ref">
        <h3 className="text-[11px] label-up text-[var(--rs-text-3)] mb-2.5">Quick Reference — 1/64&quot; increments (in · mm)</h3>
        <div className="grid grid-cols-2 gap-x-4">
          {QUICK_REF.map((r) => (
            <div key={r.frac} className="flex justify-between gap-1.5 py-0.5 border-b last:border-b-0 text-[11px] font-mono-rs"
              style={{ borderColor: "var(--rs-border)" }}>
              <span>{r.frac}&quot;</span>
              <span className="text-[var(--rs-text-2)]">{r.dec}</span>
              <span style={{ color: "var(--rs-amber)" }}>{r.mm}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card-rs p-4" data-testid="thread-reference">
        <h3 className="text-[11px] label-up text-[var(--rs-text-3)] mb-1">Thread Reference — oilfield bolting</h3>
        <p className="text-[10.5px] text-[var(--rs-text-3)] mb-2.5">
          API 6A studs: UNC below 1&quot;, 8-UN at 1&quot; and above. Pitch = 1 ÷ TPI.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {Object.entries(THREAD_REF).map(([series, rows]) => (
            <div key={series} data-testid={`thread-series-${series.toLowerCase().replace("-", "")}`}>
              <div className="flex justify-between text-[10px] label-up text-[var(--rs-amber)] pb-1 border-b"
                style={{ borderColor: "var(--rs-border)" }}>
                <span>{series}</span><span>TPI · pitch</span>
              </div>
              {rows.map(([size, tpi]) => (
                <div key={size} className="flex justify-between gap-1.5 py-0.5 border-b last:border-b-0 text-[11px] font-mono-rs"
                  style={{ borderColor: "var(--rs-border)" }}>
                  <span>{size} - {tpi} {series === "8-UN" ? "UN" : series}</span>
                  <span className="text-[var(--rs-text-2)]">{(1 / tpi).toFixed(4)}&quot; · {(25.4 / tpi).toFixed(2)} mm</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
